import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Shield,
  Calendar,
  FileText,
  Download,
  CheckCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Globe,
  Send,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import i18n from "../i18n";
import { apiClient } from "../services/api";
import { usePayrollCountry } from "../hooks/usePayrollCountry";

const COUNTRIES = [
  { code: "TN", flag: "🇹🇳" },
  { code: "QA", flag: "🇶🇦" },
  { code: "MA", flag: "🇲🇦" },
  { code: "FR", flag: "🇫🇷" },
  { code: "SA", flag: "🇸🇦" },
];

const TABS = [
  { id: "calendar", icon: Calendar },
  { id: "cnss", icon: Shield },
  { id: "cnss_ma", icon: Shield },
  { id: "wps", icon: Globe },
  { id: "gosi", icon: Globe },
  { id: "dsn", icon: Send },
  { id: "history", icon: FileText },
];

// Each declaration tab is specific to one country. A tenant only sees the
// obligations that concern its own country (plus the generic calendar/history).
const TAB_COUNTRY = {
  cnss: "TN",      // CNSS / CNAM — Tunisia
  cnss_ma: "MA",   // CNSS — Morocco
  wps: "QA",       // WPS — Qatar
  gosi: "SA",      // GOSI — Saudi Arabia
  dsn: "FR",       // DSN — France
};

const STATUS_VARIANTS = {
  upcoming: "secondary", overdue: "destructive", draft: "outline",
  submitted: "secondary", accepted: "default", rejected: "destructive",
};

const statusBadge = (s, t) => {
  const variant = STATUS_VARIANTS[s] || "outline";
  return <Badge variant={variant}>{t(`complianceHub.status.${s}`, { defaultValue: s })}</Badge>;
};

const nowYM = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

// ---------------------------------------------------------------------------
// Tab: Calendrier
// ---------------------------------------------------------------------------
function CalendarTab() {
  const { t } = useTranslation();
  const { countryCode } = usePayrollCountry();
  const [country, setCountry] = useState(countryCode);
  // Default the calendar to the tenant's own country once it resolves.
  useEffect(() => { setCountry(countryCode); }, [countryCode]);
  const [period, setPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [y, m] = period.split("-");
      const r = await apiClient.get(
        `v1/compliance/calendar?country=${country}&year=${y}&month=${m}`
      );
      setDeadlines(r.data.deadlines || []);
    } catch {
      setDeadlines([]);
    } finally {
      setLoading(false);
    }
  }, [country, period]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-sm text-muted-foreground block mb-1">{t("complianceHub.calendar.country")}</label>
          <select
            className="border rounded-md px-3 py-2 text-sm bg-background"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          >
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{c.flag} {t(`complianceHub.countries.${c.code}`)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm text-muted-foreground block mb-1">{t("complianceHub.period")}</label>
          <input
            type="month"
            className="border rounded-md px-3 py-2 text-sm bg-background"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          />
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
          {t("common.refresh")}
        </Button>
      </div>

      {deadlines.length === 0 ? (
        <p className="text-muted-foreground text-sm py-8 text-center">
          {t("complianceHub.calendar.empty")}
        </p>
      ) : (
        <div className="space-y-3">
          {deadlines.map((d) => (
            <div
              key={d.id}
              className="flex items-center justify-between p-4 border rounded-lg bg-card"
            >
              <div className="flex items-center gap-3">
                {d.status === "overdue" ? (
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                ) : (
                  <Clock className="h-5 w-5 text-muted-foreground" />
                )}
                <div>
                  <p className="font-medium text-sm">{d.label}</p>
                  <p className="text-xs text-muted-foreground">
                    {t("complianceHub.calendar.dueDate", { date: new Date(d.due_date).toLocaleDateString() })}
                    {" · "}
                    <span className="capitalize">{d.frequency}</span>
                  </p>
                </div>
              </div>
              {statusBadge(d.status, t)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: CNSS Tunisia
// ---------------------------------------------------------------------------
function CnssTab() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState(nowYM);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const generate = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiClient.post(`v1/compliance/cnss/generate?period=${period}&save=true`);
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || t("complianceHub.genError"));
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    if (!result?.declaration_id) return;
    window.open(
      `${process.env.REACT_APP_API_URL}/v1/compliance/cnss/${result.declaration_id}/download`,
      "_blank"
    );
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-800 dark:text-blue-300">
        <strong>{t("complianceHub.cnss.bannerTitle")}</strong>{t("complianceHub.cnss.bannerText")}
      </div>

      <div className="flex items-end gap-3">
        <div>
          <label className="text-sm text-muted-foreground block mb-1">{t("complianceHub.period")}</label>
          <input
            type="month"
            className="border rounded-md px-3 py-2 text-sm bg-background"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          />
        </div>
        <Button onClick={generate} disabled={loading}>
          {loading ? (
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Send className="h-4 w-4 mr-2" />
          )}
          {t("complianceHub.generate")}
        </Button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {result && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: t("complianceHub.kpiEmployees"), value: result.total_employees },
              { label: t("complianceHub.cnss.kpiGross"), value: `${Number(result.total_gross || 0).toLocaleString()} TND` },
              { label: t("complianceHub.cnss.kpiEmpContrib"), value: `${Number(result.total_employee_contributions || 0).toLocaleString()} TND` },
              { label: t("complianceHub.cnss.kpiErContrib"), value: `${Number(result.total_employer_contributions || 0).toLocaleString()} TND` },
            ].map((k) => (
              <div key={k.label} className="p-3 border rounded-lg bg-card text-center">
                <p className="text-lg font-bold">{k.value}</p>
                <p className="text-xs text-muted-foreground">{k.label}</p>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <span className="text-sm text-green-700 dark:text-green-400">
              {t("complianceHub.cnss.declGenerated", { id: result.declaration_id, file: result.file_name })}
            </span>
            <Button variant="outline" size="sm" onClick={download} className="ml-auto">
              <Download className="h-4 w-4 mr-1" /> {t("complianceHub.downloadCsv")}
            </Button>
          </div>

          {result.rows?.length > 0 && (
            <div className="border rounded-lg overflow-auto max-h-72">
              <table className="w-full text-xs">
                <thead className="bg-muted">
                  <tr>
                    {[t("complianceHub.cnss.colMatricule"), t("complianceHub.cnss.colName"), t("complianceHub.cnss.colCnss"), t("complianceHub.cnss.colCin"), t("complianceHub.cnss.colGross"), t("complianceHub.cnss.colEmpCot"), t("complianceHub.cnss.colErCot")].map((h) => (
                      <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((r, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-3 py-1.5">{r.matricule}</td>
                      <td className="px-3 py-1.5">{r.nom_prenom}</td>
                      <td className="px-3 py-1.5">{r.cnss_number || "—"}</td>
                      <td className="px-3 py-1.5">{r.cin || "—"}</td>
                      <td className="px-3 py-1.5 text-right">{Number(r.salaire_brut).toLocaleString(i18n.language)}</td>
                      <td className="px-3 py-1.5 text-right">{Number(r.cotisation_salariale).toLocaleString(i18n.language)}</td>
                      <td className="px-3 py-1.5 text-right">{Number(r.cotisation_patronale).toLocaleString(i18n.language)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: WPS Qatar
// ---------------------------------------------------------------------------
function WpsTab() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState(nowYM);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const generate = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiClient.post(`v1/compliance/wps/generate?period=${period}&save=true`);
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || t("complianceHub.genError"));
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    if (!result?.declaration_id) return;
    window.open(
      `${process.env.REACT_APP_API_URL}/v1/compliance/wps/${result.declaration_id}/download`,
      "_blank"
    );
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="p-4 bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 rounded-lg text-sm text-purple-800 dark:text-purple-300">
        <strong>{t("complianceHub.wps.bannerTitle")}</strong>{t("complianceHub.wps.bannerText")}
      </div>

      <div className="flex items-end gap-3">
        <div>
          <label className="text-sm text-muted-foreground block mb-1">{t("complianceHub.period")}</label>
          <input
            type="month"
            className="border rounded-md px-3 py-2 text-sm bg-background"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          />
        </div>
        <Button onClick={generate} disabled={loading}>
          {loading ? (
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Send className="h-4 w-4 mr-2" />
          )}
          {t("complianceHub.wps.generateSif")}
        </Button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {result && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: t("complianceHub.kpiEmployees"), value: result.total_employees },
              { label: t("complianceHub.wps.kpiNet"), value: `${Number(result.total_net || 0).toLocaleString()} QAR` },
            ].map((k) => (
              <div key={k.label} className="p-3 border rounded-lg bg-card text-center">
                <p className="text-lg font-bold">{k.value}</p>
                <p className="text-xs text-muted-foreground">{k.label}</p>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <span className="text-sm text-green-700 dark:text-green-400">
              {t("complianceHub.wps.fileGenerated", { id: result.declaration_id, file: result.file_name })}
            </span>
            <Button variant="outline" size="sm" onClick={download} className="ml-auto">
              <Download className="h-4 w-4 mr-1" /> {t("complianceHub.wps.downloadSif")}
            </Button>
          </div>

          {result.rows?.length > 0 && (
            <div className="border rounded-lg overflow-auto max-h-64">
              <table className="w-full text-xs">
                <thead className="bg-muted">
                  <tr>
                    {[t("complianceHub.wps.colMatricule"), t("complianceHub.wps.colName"), t("complianceHub.wps.colNet")].map((h) => (
                      <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((r, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-3 py-1.5">{r.matricule}</td>
                      <td className="px-3 py-1.5">{r.nom_prenom}</td>
                      <td className="px-3 py-1.5 text-right">{Number(r.net_pay).toLocaleString(i18n.language)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: GOSI Saudi Arabia
// ---------------------------------------------------------------------------
function GosiTab() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState(nowYM);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const generate = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiClient.post(`v1/compliance/gosi/generate?period=${period}&save=true`);
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || t("complianceHub.genError"));
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    if (!result?.declaration_id) return;
    window.open(
      `${process.env.REACT_APP_API_URL}/v1/compliance/gosi/${result.declaration_id}/download`,
      "_blank"
    );
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-800 dark:text-green-300">
        <strong>{t("complianceHub.gosi.bannerTitle")}</strong>{t("complianceHub.gosi.bannerText")}
      </div>

      <div className="flex items-end gap-3">
        <div>
          <label className="text-sm text-muted-foreground block mb-1">{t("complianceHub.period")}</label>
          <input
            type="month"
            className="border rounded-md px-3 py-2 text-sm bg-background"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          />
        </div>
        <Button onClick={generate} disabled={loading}>
          {loading ? (
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Send className="h-4 w-4 mr-2" />
          )}
          {t("complianceHub.gosi.generateCsv")}
        </Button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {result && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: t("complianceHub.kpiEmployees"), value: result.total_employees },
              { label: t("complianceHub.gosi.kpiGross"), value: `${Number(result.total_gross || 0).toLocaleString()}` },
              { label: t("complianceHub.gosi.kpiEmpContrib"), value: `${Number(result.total_employee_contributions || 0).toLocaleString()}` },
              { label: t("complianceHub.gosi.kpiErContrib"), value: `${Number(result.total_employer_contributions || 0).toLocaleString()}` },
            ].map((k) => (
              <div key={k.label} className="p-3 border rounded-lg bg-card text-center">
                <p className="text-base font-bold">{k.value}</p>
                <p className="text-xs text-muted-foreground">{k.label}</p>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <span className="text-sm text-green-700 dark:text-green-400">
              {t("complianceHub.gosi.fileGenerated", { id: result.declaration_id, file: result.file_name })}
            </span>
            <Button variant="outline" size="sm" onClick={download} className="ml-auto">
              <Download className="h-4 w-4 mr-1" /> {t("complianceHub.downloadCsv")}
            </Button>
          </div>

          {result.rows?.length > 0 && (
            <div className="border rounded-lg overflow-auto max-h-72">
              <table className="w-full text-xs">
                <thead className="bg-muted">
                  <tr>
                    {[t("complianceHub.gosi.colMatricule"), t("complianceHub.gosi.colName"), t("complianceHub.gosi.colType"), t("complianceHub.gosi.colBase"), t("complianceHub.gosi.colEmpCot"), t("complianceHub.gosi.colErCot"), t("complianceHub.gosi.colTotal")].map((h) => (
                      <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((r, i) => (
                    <tr key={i} className="border-t hover:bg-muted/30">
                      <td className="px-3 py-1.5">{r.matricule}</td>
                      <td className="px-3 py-1.5">{r.nom_prenom}</td>
                      <td className="px-3 py-1.5">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${r.type === "Salarié" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"}`}>
                          {r.type}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-right">{Number(r.salaire_de_base).toLocaleString(i18n.language)}</td>
                      <td className="px-3 py-1.5 text-right">{Number(r.cotisation_salarie).toLocaleString(i18n.language)}</td>
                      <td className="px-3 py-1.5 text-right">{Number(r.cotisation_employeur).toLocaleString(i18n.language)}</td>
                      <td className="px-3 py-1.5 text-right font-medium">{Number(r.total).toLocaleString(i18n.language)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: CNSS Morocco
// ---------------------------------------------------------------------------
function CnssMarocTab() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState(nowYM);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const generate = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiClient.post(`v1/compliance/cnss-ma/generate?period=${period}&save=true`);
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || t("complianceHub.genError"));
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    if (!result?.declaration_id) return;
    window.open(
      `${process.env.REACT_APP_API_URL}/v1/compliance/cnss-ma/${result.declaration_id}/download`,
      "_blank"
    );
  };

  const COMPONENTS = [
    { id: "allocations_familiales",    label: t("complianceHub.cnssMa.compAllocations") },
    { id: "court_terme",               label: t("complianceHub.cnssMa.compCourtTerme") },
    { id: "long_terme",                label: t("complianceHub.cnssMa.compLongTerme") },
    { id: "amo",                       label: t("complianceHub.cnssMa.compAmo") },
    { id: "formation_professionnelle", label: t("complianceHub.cnssMa.compFormation") },
  ];

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-800 dark:text-red-300">
        <strong>{t("complianceHub.cnssMa.bannerTitle")}</strong>{t("complianceHub.cnssMa.bannerText")}
      </div>

      <div className="flex items-end gap-3">
        <div>
          <label className="text-sm text-muted-foreground block mb-1">{t("complianceHub.period")}</label>
          <input
            type="month"
            className="border rounded-md px-3 py-2 text-sm bg-background"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          />
        </div>
        <Button onClick={generate} disabled={loading}>
          {loading ? (
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Send className="h-4 w-4 mr-2" />
          )}
          {t("complianceHub.cnssMa.generateCsv")}
        </Button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {result && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: t("complianceHub.kpiEmployees"), value: result.total_employees },
              { label: t("complianceHub.cnssMa.kpiGross"), value: `${Number(result.total_gross || 0).toLocaleString()}` },
              { label: t("complianceHub.cnssMa.kpiEmpContrib"), value: `${Number(result.total_employee_contributions || 0).toLocaleString()}` },
              { label: t("complianceHub.cnssMa.kpiErContrib"), value: `${Number(result.total_employer_contributions || 0).toLocaleString()}` },
            ].map((k) => (
              <div key={k.label} className="p-3 border rounded-lg bg-card text-center">
                <p className="text-base font-bold">{k.value}</p>
                <p className="text-xs text-muted-foreground">{k.label}</p>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <span className="text-sm text-green-700 dark:text-green-400">
              {t("complianceHub.cnssMa.declGenerated", { id: result.declaration_id, file: result.file_name })}
            </span>
            <Button variant="outline" size="sm" onClick={download} className="ml-auto">
              <Download className="h-4 w-4 mr-1" /> {t("complianceHub.downloadCsv")}
            </Button>
          </div>

          {result.rows?.length > 0 && (
            <div className="border rounded-lg overflow-auto max-h-72">
              <table className="w-full text-xs">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">{t("complianceHub.cnssMa.colMatricule")}</th>
                    <th className="px-3 py-2 text-left font-medium">{t("complianceHub.cnssMa.colName")}</th>
                    <th className="px-3 py-2 text-left font-medium">{t("complianceHub.cnssMa.colCnss")}</th>
                    <th className="px-3 py-2 text-right font-medium">{t("complianceHub.cnssMa.colGross")}</th>
                    <th className="px-3 py-2 text-right font-medium">{t("complianceHub.cnssMa.colCapped")}</th>
                    {COMPONENTS.map((c) => (
                      <th key={c.id} className="px-3 py-2 text-right font-medium" title={c.label}>
                        {c.label}
                      </th>
                    ))}
                    <th className="px-3 py-2 text-right font-medium">{t("complianceHub.cnssMa.colTotalEmp")}</th>
                    <th className="px-3 py-2 text-right font-medium">{t("complianceHub.cnssMa.colTotalEr")}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((r, i) => (
                    <tr key={i} className="border-t hover:bg-muted/30">
                      <td className="px-3 py-1.5">{r.matricule}</td>
                      <td className="px-3 py-1.5">{r.nom_prenom}</td>
                      <td className="px-3 py-1.5">{r.cnss_number || "—"}</td>
                      <td className="px-3 py-1.5 text-right">{Number(r.salaire_brut).toLocaleString(i18n.language)}</td>
                      <td className="px-3 py-1.5 text-right">{Number(r["salaire_plafonné"]).toLocaleString(i18n.language)}</td>
                      {COMPONENTS.map((c) => (
                        <td key={c.id} className="px-3 py-1.5 text-right">
                          {Number(r[`salarie_${c.id}`] || 0).toLocaleString(i18n.language)}
                        </td>
                      ))}
                      <td className="px-3 py-1.5 text-right font-medium">{Number(r.total_salarie).toLocaleString(i18n.language)}</td>
                      <td className="px-3 py-1.5 text-right font-medium">{Number(r.total_patronal).toLocaleString(i18n.language)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: DSN France
// ---------------------------------------------------------------------------
function DsnTab() {
  const { t } = useTranslation();
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [siren, setSiren] = useState("");
  const [siret, setSiret] = useState("");
  const [raison, setRaison] = useState("");
  const [result, setResult] = useState(null);
  const [calendar, setCalendar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiClient.get(`v1/dsn/calendar?year=${year}`).then((r) => setCalendar(r.data)).catch(() => {});
  }, [year]);

  const generate = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiClient.post("v1/dsn/generate", {
        year: parseInt(year), month: parseInt(month),
        siren: siren || undefined, siret: siret || undefined, raison_sociale: raison || undefined,
      });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || t("complianceHub.genError"));
    } finally {
      setLoading(false);
    }
  };

  const download = async () => {
    const r = await apiClient.post("v1/dsn/generate/download", {
      year: parseInt(year), month: parseInt(month),
      siren: siren || undefined, siret: siret || undefined, raison_sociale: raison || undefined,
    }, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = `DSN_${year}_${String(month).padStart(2, "0")}.txt`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const MONTHS = Array.from({ length: 12 }, (_, i) => t(`complianceHub.dsn.months.m${i + 1}`));

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-800 dark:text-blue-300">
        <strong>{t("complianceHub.dsn.bannerTitle")}</strong>{t("complianceHub.dsn.bannerText")}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div>
          <label className="text-sm text-muted-foreground block mb-1">{t("complianceHub.dsn.year")}</label>
          <input type="number" className="w-full border rounded-md px-3 py-2 text-sm bg-background"
            value={year} onChange={(e) => setYear(e.target.value)} min={2020} max={2030} />
        </div>
        <div>
          <label className="text-sm text-muted-foreground block mb-1">{t("complianceHub.dsn.month")}</label>
          <select className="w-full border rounded-md px-3 py-2 text-sm bg-background"
            value={month} onChange={(e) => setMonth(e.target.value)}>
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="text-sm text-muted-foreground block mb-1">{t("complianceHub.dsn.siren")}</label>
          <input className="w-full border rounded-md px-3 py-2 text-sm bg-background"
            value={siren} onChange={(e) => setSiren(e.target.value)} placeholder={t("complianceHub.dsn.sirenPlaceholder")} maxLength={9} />
        </div>
        <div>
          <label className="text-sm text-muted-foreground block mb-1">{t("complianceHub.dsn.siret")}</label>
          <input className="w-full border rounded-md px-3 py-2 text-sm bg-background"
            value={siret} onChange={(e) => setSiret(e.target.value)} placeholder={t("complianceHub.dsn.siretPlaceholder")} maxLength={14} />
        </div>
        <div className="col-span-2">
          <label className="text-sm text-muted-foreground block mb-1">{t("complianceHub.dsn.raison")}</label>
          <input className="w-full border rounded-md px-3 py-2 text-sm bg-background"
            value={raison} onChange={(e) => setRaison(e.target.value)} placeholder={t("complianceHub.dsn.raisonPlaceholder")} />
        </div>
      </div>

      <div className="flex gap-3">
        <Button onClick={generate} disabled={loading}>
          {loading ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> {t("complianceHub.dsn.generating")}</> : <><Send className="h-4 w-4 mr-2" /> {t("complianceHub.generate")}</>}
        </Button>
        {result && (
          <Button variant="outline" onClick={download}>
            <Download className="h-4 w-4 mr-2" /> {t("complianceHub.dsn.downloadTxt")}
          </Button>
        )}
      </div>

      {error && <div className="p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">{error}</div>}

      {result && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div className="bg-card border border-border rounded-lg p-3 text-center">
              <p className="text-2xl font-bold">{result.employee_count}</p>
              <p className="text-xs text-muted-foreground">{t("complianceHub.dsn.kpiEmployees")}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-3 text-center">
              <p className="text-2xl font-bold">{result.total_remuneration?.toLocaleString()} €</p>
              <p className="text-xs text-muted-foreground">{t("complianceHub.dsn.kpiGross")}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-3 text-center">
              <p className="text-2xl font-bold">{result.total_cotisations?.toLocaleString()} €</p>
              <p className="text-xs text-muted-foreground">{t("complianceHub.dsn.kpiCotis")}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-3 text-center">
              <p className="text-sm font-semibold">{result.period}</p>
              <p className="text-xs text-muted-foreground">{t("complianceHub.dsn.kpiPeriod")}</p>
            </div>
          </div>
          <div className="p-3 bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg text-xs text-yellow-800 dark:text-yellow-300">
            {result.disclaimer}
          </div>
          <div>
            <p className="text-sm font-medium mb-2">{t("complianceHub.dsn.preview")}</p>
            <pre className="text-xs bg-muted p-4 rounded-lg overflow-auto max-h-64 font-mono">{result.dsn_text?.slice(0, 1500)}{result.dsn_text?.length > 1500 ? "\n..." : ""}</pre>
          </div>
        </div>
      )}

      {calendar.length > 0 && (
        <div>
          <p className="text-sm font-medium mb-2 flex items-center gap-2"><Calendar className="h-4 w-4" /> {t("complianceHub.dsn.calendarTitle", { year })}</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {calendar.map((c) => (
              <div key={c.period} className={`text-xs p-2 rounded-lg border text-center ${c.is_past ? "border-border bg-muted/30 text-muted-foreground" : "border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30"}`}>
                <p className="font-medium">{c.period_label}</p>
                <p className="text-muted-foreground">{c.due_date_large}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Historique
// ---------------------------------------------------------------------------
function HistoryTab() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [typeFilter, setTypeFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = typeFilter ? `?declaration_type=${typeFilter}` : "";
      const r = await apiClient.get(`v1/compliance/declarations${q}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    await apiClient.patch(`v1/compliance/declarations/${id}/status?status=${status}`);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <select
          className="border rounded-md px-3 py-2 text-sm bg-background"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="">{t("complianceHub.history.allTypes")}</option>
          <option value="cnss_tn">{t("complianceHub.declTypes.cnss_tn")}</option>
          <option value="cnss_ma">{t("complianceHub.declTypes.cnss_ma")}</option>
          <option value="wps_qa">{t("complianceHub.declTypes.wps_qa")}</option>
          <option value="gosi_sa">{t("complianceHub.declTypes.gosi_sa")}</option>
        </select>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
        <span className="text-sm text-muted-foreground ml-auto">{t("complianceHub.history.count", { count: total })}</span>
      </div>

      {items.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-8">
          {t("complianceHub.history.empty")}
        </p>
      ) : (
        <div className="space-y-2">
          {items.map((d) => (
            <div key={d.id} className="border rounded-lg bg-card overflow-hidden">
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30"
                onClick={() => setExpanded(expanded === d.id ? null : d.id)}
              >
                <div className="flex items-center gap-3">
                  {expanded === d.id ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                  <div>
                    <p className="font-medium text-sm">
                      {t(`complianceHub.declTypes.${d.declaration_type}`, { defaultValue: d.declaration_type })} — {d.period}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t("complianceHub.history.employees", { count: d.total_employees })} ·{" "}
                      {new Date(d.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {statusBadge(d.status, t)}
                </div>
              </div>

              {expanded === d.id && (
                <div className="px-4 pb-4 pt-0 border-t bg-muted/20 space-y-3">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3">
                    {[
                      { label: t("complianceHub.history.colEmployees"), value: d.total_employees },
                      { label: t("complianceHub.history.colGross"), value: d.total_gross ? `${Number(d.total_gross).toLocaleString()}` : "—" },
                      { label: t("complianceHub.history.colEmpCot"), value: d.total_employee_contributions ? `${Number(d.total_employee_contributions).toLocaleString()}` : "—" },
                      { label: t("complianceHub.history.colErCot"), value: d.total_employer_contributions ? `${Number(d.total_employer_contributions).toLocaleString()}` : "—" },
                    ].map((k) => (
                      <div key={k.label} className="text-center">
                        <p className="text-sm font-semibold">{k.value}</p>
                        <p className="text-xs text-muted-foreground">{k.label}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {d.status === "draft" && (
                      <Button size="sm" variant="outline" onClick={() => updateStatus(d.id, "submitted")}>
                        {t("complianceHub.history.markSubmitted")}
                      </Button>
                    )}
                    {d.status === "submitted" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => updateStatus(d.id, "accepted")}>
                          <CheckCircle className="h-3.5 w-3.5 mr-1" /> {t("complianceHub.history.accepted")}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => updateStatus(d.id, "rejected")}>
                          {t("complianceHub.history.rejected")}
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        window.open(
                          `${process.env.REACT_APP_API_URL}/v1/compliance/${
                            { cnss_tn: "cnss", cnss_ma: "cnss-ma", wps_qa: "wps", gosi_sa: "gosi" }[d.declaration_type] || "cnss"
                          }/${d.id}/download`,
                          "_blank"
                        )
                      }
                    >
                      <Download className="h-3.5 w-3.5 mr-1" /> {t("complianceHub.history.download")}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Hub
// ---------------------------------------------------------------------------
export default function ComplianceHub() {
  const { t } = useTranslation();
  const { countryCode } = usePayrollCountry();
  const [searchParams, setSearchParams] = useSearchParams();

  // Only show the declaration tab(s) that concern the tenant's country.
  const visibleTabs = TABS.filter(
    ({ id }) => !TAB_COUNTRY[id] || TAB_COUNTRY[id] === countryCode
  );

  const requestedTab = searchParams.get("tab") || "calendar";
  // Fall back to the calendar if the URL points to a tab hidden for this country.
  const activeTab = visibleTabs.some((tb) => tb.id === requestedTab)
    ? requestedTab
    : "calendar";

  const setTab = (id) => setSearchParams({ tab: id });

  const renderContent = () => {
    switch (activeTab) {
      case "calendar": return <CalendarTab />;
      case "cnss":     return <CnssTab />;
      case "cnss_ma":  return <CnssMarocTab />;
      case "wps":      return <WpsTab />;
      case "gosi":     return <GosiTab />;
      case "dsn":      return <DsnTab />;
      case "history":  return <HistoryTab />;
      default:         return <CalendarTab />;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="h-6 w-6 text-primary" />
          {t("complianceHub.title")}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          {t("complianceHub.subtitle")}
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b flex gap-1">
        {visibleTabs.map(({ id, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === id
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Icon className="h-4 w-4" />
            {t(`complianceHub.tabs.${id}`)}
          </button>
        ))}
      </div>

      {/* Content */}
      <div>{renderContent()}</div>
    </div>
  );
}
